import React, { FocusEvent, MouseEvent, useState } from 'react';
import { HiChevronDown } from '@react-icons/all-files/hi/HiChevronDown';
import { HiChevronRight } from '@react-icons/all-files/hi/HiChevronRight';
import { RiCloseCircleFill } from '@react-icons/all-files/ri/RiCloseCircleFill';
import { RiDeleteBinLine } from '@react-icons/all-files/ri/RiDeleteBinLine';
import { RiFileCopyLine } from '@react-icons/all-files/ri/RiFileCopyLine';
import { RiShapeLine } from '@react-icons/all-files/ri/RiShapeLine';
import { useContextSelector } from 'use-context-selector';

import {
  cloneNodeCommand,
  createNodeCommand,
  removeNodeCommand,
} from '../../commands';
// import { EditorContext } from '../EditorContext';
import { EditorContext } from '../../contexts';
import { useEditorNode } from '../../hooks/useEditorNode';
import { COLORS } from '../../styles';
import { DocumentNode, DocumentPath } from '../../types';
import { findAncestorNodes } from '../../utils';
import Toolbar from '../Toolbar';
import Draggable from './Draggable';
import * as S from './TreeList.style';

export interface TreeListItemProps {
  node: DocumentNode;
  path: DocumentPath;
  isParentSelected?: boolean;
  isParentDisabled?: boolean;
}

function TreeListItemImpl({
  node,
  path,
  isParentSelected,
  isParentDisabled,
}: TreeListItemProps): React.ReactElement {
  const { components, dispatch, getState } = useContextSelector(
    EditorContext,
    (context) => context!,
  );
  const Component = components[node.type];

  const { document } = getState();
  const { isSelected, onSelect, isHovered, onHover, onUnhover } =
    useEditorNode(path);

  function selectItem(e: MouseEvent) {
    e.stopPropagation();
    onSelect();
  }

  function handleFocus(e: MouseEvent | FocusEvent) {
    e.stopPropagation();
    if (isHovered) { return; }
    onHover(path);
  }

  function handleBlur() {
    onUnhover();
  }

  const [isExpanded, setExpanded] = useState(true);
  function toggleExpand() {
    setExpanded((e) => !e);
  }

  function cloneItem(e: MouseEvent) {
    e.stopPropagation();
    dispatch(cloneNodeCommand(path));
  }

  function deleteItem(e: MouseEvent) {
    e.stopPropagation();
    dispatch(removeNodeCommand(path));
  }

  function insertItem(type: string) {
    dispatch(createNodeCommand(type, [...path, node.children.length]));
  }

  /**
   * 지원하지 않는 컴포넌트
   */
  if (!Component) {
    return (
      <S.Item style={{ cursor: 'default' }}>
        <S.ItemRow>
          <S.IconButton
            type="button"
            width={12}
            style={{ marginRight: '6px' }}
            title={isExpanded ? '펼치기' : '접기'}
            hidden={!node.children.length}
            onClick={toggleExpand}
          >
            { isExpanded ? <HiChevronDown /> : <HiChevronRight /> }
          </S.IconButton>
          <S.Icon>
            <RiCloseCircleFill style={{ color: 'red' }} />
          </S.Icon>
          <S.Label error data-error="Unsupported">{ node.type }</S.Label>
          <S.IconButton
            type="button"
            title="삭제"
            onClick={deleteItem}
          >
            <RiDeleteBinLine />
          </S.IconButton>
        </S.ItemRow>
        { isExpanded && (
          <S.SubList>
            { node.children.map((child, i) => (
              <TreeListItem
                key={child.key ?? i}
                node={child}
                path={[...path, i]}
                isParentDisabled
              />
            )) }
          </S.SubList>
        ) }
      </S.Item>
    );
  }

  /**
   * 지원하지 않는 컴포넌트의 자식
   */
  if (isParentDisabled) {
    return (
      <S.Item style={{ cursor: 'default', color: '#b5bbc1' }}>
        <S.ItemRow>
          <S.IconButton
            type="button"
            width={12}
            style={{ marginRight: '6px' }}
            title={isExpanded ? '펼치기' : '접기'}
            hidden={!node.children.length}
            onClick={toggleExpand}
          >
            { isExpanded ? <HiChevronDown /> : <HiChevronRight /> }
          </S.IconButton>
          <S.Icon>
            { Component.renderIcon?.(node.props) || <RiShapeLine /> }
          </S.Icon>
          <S.Label lineThrough>
            { Component.renderTitle?.(node.props) || Component.label }
          </S.Label>
        </S.ItemRow>
        { isExpanded && (
          <S.SubList>
            { node.children.map((child, i) => (
              <TreeListItem
                key={child.key ?? i}
                node={child}
                path={[...path, i]}
                isParentDisabled
              />
            )) }
          </S.SubList>
        ) }
      </S.Item>
    );
  }

  return (
    <S.Item
      isSelected={isSelected}
      onClick={selectItem}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseOver={handleFocus}
      onMouseLeave={handleBlur}
    >
      <Draggable path={path} disabled={Component.disabledMove}>
        <S.ItemRow hasBorder={isHovered}>
          <S.IconButton
            type="button"
            width={12}
            style={{ marginRight: '6px' }}
            title={isExpanded ? '펼치기' : '접기'}
            hidden={!node.children.length}
            onClick={toggleExpand}
          >
            { isExpanded
              ? (
                <HiChevronDown color={isSelected ? COLORS.blue60 : undefined} />
              )
              : (
                <HiChevronRight />
              ) }
          </S.IconButton>
          <S.Icon>
            { Component.renderIcon?.(node.props) || <RiShapeLine /> }
          </S.Icon>
          <S.Label bold={isSelected}>
            { Component.renderTitle?.(node.props) ||
              Component.label ||
              node.type }
          </S.Label>
          { !Component.disabledClone && (
            <S.IconButton
              type="button"
              title="복제"
              hidden={!isHovered}
              onClick={cloneItem}
            >
              <RiFileCopyLine />
            </S.IconButton>
          ) }
          { !Component.disabledDelete && (
            <S.IconButton
              type="button"
              title="삭제"
              hidden={!isHovered}
              onClick={deleteItem}
            >
              <RiDeleteBinLine />
            </S.IconButton>
          ) }
        </S.ItemRow>
        { isExpanded && (
          <S.SubList hasLeftBorder={isSelected || isParentSelected}>
            { node.children.map((child, i) => (
              <TreeListItem
                key={child.key ?? i}
                node={child}
                path={[...path, i]}
                isParentSelected={isSelected || isParentSelected}
              />
            )) }
          </S.SubList>
        ) }
      </Draggable>
      { isSelected && (
        <Toolbar
          tools={(Component.allowedChildren ?? Object.keys(components)).filter(
            (child) => {
              const childComponent = components[child];
              const ancestors = findAncestorNodes(document, path).map(
                ({ type }) => type,
              );
              const hasDisallowedAncestors = (
                childComponent?.disallowedAncestors ?? []
              ).some((a) => ancestors.indexOf(a) !== -1);

              return (
                !hasDisallowedAncestors &&
                (childComponent?.allowedParent == null ||
                  childComponent.allowedParent.includes(node.type))
              );
            },
          )}
          onSelect={insertItem}
        />
      ) }
    </S.Item>
  );
}

export const TreeListItem = React.memo(TreeListItemImpl);
